import {Active, Over} from '@dnd-kit/core';
import {useCallback} from 'react';
import {VirtuosoHandle} from 'react-virtuoso';

import {useViewAction} from '../../state/views/hooks';
import * as Actions from '../../state/views/panelBankConfig/actions';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {PanelDropRef, SectionWithRef} from './types';
import {getDndSortableSectionId} from './utils';

export const useHandleSectionDragEnd = ({
  visibleSections,
  virtuosoRef,
}: {
  visibleSections: SectionWithRef[];
  virtuosoRef: React.RefObject<VirtuosoHandle>;
}) => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();

  const moveSection = useViewAction(panelBankConfigRef, Actions.moveSection);

  const handleSectionDragEnd = useCallback(
    (active: Active | null, over: Over | null) => {
      const activeSectionRefId = active?.id.toString();
      const targetSectionRefId = over?.id.toString();

      if (!activeSectionRefId || !targetSectionRefId) {
        return;
      }

      moveSection(activeSectionRefId, targetSectionRefId);

      const index = visibleSections.findIndex(
        section => getDndSortableSectionId(section.ref) === over?.id
      );
      if (index >= 0) {
        virtuosoRef.current?.scrollToIndex({
          index,
          align: 'start',
          behavior: 'auto', // Smooth behavior is less performant, so please check for regressions if you change this value.
        });
      }
    },
    [moveSection, visibleSections, virtuosoRef]
  );

  return handleSectionDragEnd;
};

export const useHandlePanelDragEnd = ({
  panelDropRef,
}: {
  panelDropRef: React.RefObject<PanelDropRef | null>;
}) => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();

  // TODO - we should be able to move all panel drag end events to use `movePanelToDiffSection`
  // It should get renamed and handle moving panels within same section too.
  // https://wandb.atlassian.net/browse/WB-26436
  const movePanel = useViewAction(panelBankConfigRef, Actions.movePanel2);
  const movePanelToDiffSection = useViewAction(
    panelBankConfigRef,
    Actions.movePanelToDiffSection
  );

  const handlePanelDragEnd = useCallback(
    (active: Active | null, over: Over | null) => {
      const activeData = active?.data.current;
      const targetData = over?.data.current;

      const isSameSection =
        activeData?.sectionRef?.id === targetData?.sectionRef?.id;

      // This handles moving a panel within the same section
      // We need to have both panelsRefs in order to perform the action
      if (isSameSection && activeData?.panelRef && targetData?.panelRef) {
        movePanel(
          {
            panelRef: activeData.panelRef,
            sectionRef: activeData.sectionRef,
          },
          {
            panelRef: targetData.panelRef,
            sectionRef: targetData.sectionRef,
          }
        );
      }

      // This handles moving a panel to a different section. The target panelRef can be empty.
      // We cannot rely on the "over" type to determine where a panel should be inserted.
      // We use panelDropRef to determine the position more granularly and handle edge cases
      // like inserting into an empty section, beginning of a section, or end of a section.
      if (
        panelDropRef.current &&
        activeData?.panelRef &&
        targetData?.sectionRef
      ) {
        movePanelToDiffSection(
          {
            panelRef: activeData.panelRef,
            sectionRef: activeData.sectionRef,
          },
          {
            panelRef: panelDropRef.current.panelRef || null,
            sectionRef: panelDropRef.current.sectionRef,
            insertDir: panelDropRef.current.insertDir,
          }
        );
      }
    },
    [panelDropRef, movePanel, movePanelToDiffSection]
  );

  return handlePanelDragEnd;
};
